import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Save, Globe } from 'lucide-react'
import merchantService, { CreateOrganizationDto, UpdateOrganizationDto } from '@/services/merchantService'
import {
  useInputValidation,
  businessNameRules,
  descriptionRules,
  getInputClassName,
  formatBusinessName
} from '@/hooks/useInputValidation'
import { DetailHeader } from '@/components/ui/DetailHeader'

const OrganizationForm = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditMode = !!id

  // Validation hooks
  const name = useInputValidation('', businessNameRules, [isEditMode])
  const description = useInputValidation('', descriptionRules, [isEditMode])
  
  const [isActive, setIsActive] = useState(true)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (isEditMode) {
      loadOrganization()
    }
  }, [id])

  const loadOrganization = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const organization = await merchantService.getOrganizationById(id!)
      name.setValue(organization.name)
      description.setValue(organization.description || '')
      setIsActive(organization.is_active)
    } catch (err: any) {
      console.error('Error loading organization:', err)
      setError(err.response?.data?.error || 'Erreur lors du chargement de l\'organisation')
    } finally {
      setLoading(false)
    }
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatBusinessName(e.target.value)
    name.setValue(formatted)
  }

  const validateForm = (): boolean => {
    // Validate all fields
    const isNameValid = name.validate()
    description.validate() // Validate but don't need to store result
    
    if (!isNameValid) {
      setError('Veuillez corriger les erreurs dans le formulaire')
      return false
    }
    
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      if (isEditMode) {
        const updateData: UpdateOrganizationDto = {
          name: name.value,
          description: description.value || undefined,
          is_active: isActive
        }
        await merchantService.updateOrganization(id!, updateData)
        setSuccess('Organisation mise à jour avec succès')
        setTimeout(() => {
          navigate('/organizations')
        }, 1500)
      } else {
        const createData: CreateOrganizationDto = {
          name: name.value,
          description: description.value || undefined,
          is_active: isActive
        }
        await merchantService.createOrganization(createData)
        setSuccess('Organisation créée avec succès')
        setTimeout(() => {
          navigate('/organizations')
        }, 1500)
      }
    } catch (err: any) {
      console.error('Error saving organization:', err)
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const handleSaveClick = () => {
    // Create a synthetic event to pass to handleSubmit
    const syntheticEvent = { preventDefault: () => {} } as React.FormEvent
    handleSubmit(syntheticEvent)
  }

  return (
    <div className="space-y-6">
      <DetailHeader
        title={isEditMode ? 'Modifier l\'organisation' : 'Nouvelle organisation'}
        subtitle={isEditMode
          ? 'Modifiez les informations de cette organisation globale'
          : 'Créez une nouvelle organisation (marque globale) pour regrouper des marchands'}
        backUrl="/organizations"
        actions={[
          {
            label: 'Annuler',
            onClick: () => navigate('/organizations'),
            variant: 'outline' as const
          },
          {
            label: isEditMode ? 'Mettre à jour' : 'Créer l\'organisation',
            onClick: handleSaveClick,
            disabled: saving
          }
        ]}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erreur</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Information</h3>
              <div className="mt-2 text-sm text-green-700">
                <p>{success}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 gap-6">
            {/* Organization Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Nom de l'organisation <span className="text-red-500">*</span>
                {name.isValid && name.touched && (
                  <span className="ml-2 text-green-500 text-sm">✓ Valide</span>
                )}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                className={getInputClassName(!!name.error, name.isValid && name.touched, "block w-full px-3 py-2 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm")}
                placeholder="Ex: Groupe Carrefour, Amazon, etc."
                value={name.value}
                onChange={handleNameChange}
                onBlur={name.handleBlur}
              />
              {name.error && (
                <p className="mt-1 text-sm text-red-600">
                  {name.error}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Le nom de l'organisation (marque globale) qui sera visible par les marchands
              </p>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
                {description.isValid && description.touched && (
                  <span className="ml-2 text-green-500 text-sm">✓ Valide</span>
                )}
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                className={getInputClassName(!!description.error, description.isValid && description.touched, "block w-full px-3 py-2 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm")}
                placeholder="Description de l'organisation, ses valeurs, son positionnement..."
                value={description.value}
                onChange={description.handleChange}
                onBlur={description.handleBlur}
              />
              {description.error && (
                <p className="mt-1 text-sm text-red-600">
                  {description.error}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Description optionnelle pour fournir plus de contexte sur l'organisation
              </p>
            </div>

            {/* Status */}
            <div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                  Organisation active
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Une organisation inactive ne sera pas visible dans les listes de sélection
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/organizations')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditMode ? 'Mettre à jour' : 'Créer l\'organisation'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Organization Icon Preview */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Aperçu de l'organisation</h3>
        <div className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
          <div className="flex-shrink-0 h-16 w-16">
            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
              <Globe className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <div>
            <h4 className="text-lg font-medium text-gray-900">{name.value || 'Nom de l\'organisation'}</h4>
            <p className="text-sm text-gray-500 mt-1">
              {description.value || 'Description de l\'organisation apparaîtra ici'}
            </p>
            <div className="mt-2">
              {isActive ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Actif
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Inactif
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrganizationForm